import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDealStore } from '../../store/dealStore';
import { extractApiError } from '../../services/apiClient';
import config from '../../config';
import { useTheme } from '../../theme';
import { AnimatedIcon } from '../../components/icons';

const REJECTION_REASONS = ['Price too high', 'Timeline too long', 'Found another surveyor', 'Changed my mind', 'Other'];

export const DealConfirmationScreen: React.FC = () => {
  const { dealId } = useParams<{ dealId: string }>();
  const navigate = useNavigate();
  const { currentDeal, fetchDealDetail, acceptDeal, rejectDeal, isLoading } = useDealStore();
  const [dealValue, setDealValue] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [mode, setMode] = useState<'review' | 'accept' | 'reject'>('review');
  const [error, setError] = useState('');
  const [agreedToCommission, setAgreedToCommission] = useState(false);
  const { isDark } = useTheme();

  useEffect(() => {
    if (dealId) fetchDealDetail(dealId);
  }, [dealId]);

  const commissionRate = currentDeal?.commission_rate || config.DEFAULT_COMMISSION_RATE;
  const parsedValue = parseFloat(dealValue) || 0;
  const expectedCommission = parsedValue * commissionRate;

  const handleAccept = async () => {
    if (parsedValue <= 0) return setError('Please enter a valid deal value.');
    if (!agreedToCommission) return setError('You must agree to the commission terms before confirming.');
    setError('');
    try {
      await acceptDeal(dealId!, { deal_value: parsedValue });
      navigate(-1);
    } catch (err: any) {
      setError(extractApiError(err));
    }
  };

  const handleReject = async () => {
    setError('');
    try {
      await rejectDeal(dealId!, { reason: rejectReason || undefined });
      navigate(-1);
    } catch (err: any) {
      setError(extractApiError(err));
    }
  };

  if (!currentDeal && isLoading) {
    return (
      <div style={styles.loading}>
        <div style={styles.spinner} />
        <p style={{ color: 'var(--sc-text-secondary)' }}>Loading proposal...</p>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <button onClick={() => navigate(-1)} style={styles.backBtn} className="sc-press">
        ← Back
      </button>
      <h1 style={styles.title}>Deal Review</h1>
      {currentDeal && (
        <p style={styles.subtitle}>
          {currentDeal.request.service_type} — {currentDeal.surveyor.first_name} {currentDeal.surveyor.last_name}
        </p>
      )}

      {error && (
        <div style={styles.errorBox} className="sc-animate-toast-in">
          <AnimatedIcon name="warning" size={16} color="var(--sc-error)" />
          <span style={{ fontSize: 13, fontWeight: 500, marginLeft: 8 }}>{error}</span>
        </div>
      )}

      {mode === 'review' && (
        <div style={styles.card} className="sc-animate-scale-in">
          <h3 style={styles.cardTitle}>Deal Summary</h3>
          {currentDeal && (
            <div style={styles.fieldList}>
              <div style={styles.field}>
                <span style={styles.fieldLabel}>Requested Service</span>
                <span style={styles.fieldValue}>{currentDeal.request.service_type}</span>
              </div>
              <div style={styles.field}>
                <span style={styles.fieldLabel}>Assigned Surveyor</span>
                <span style={styles.fieldValue}>
                  {currentDeal.surveyor.first_name} {currentDeal.surveyor.last_name}
                </span>
              </div>
              <div style={styles.field}>
                <span style={styles.fieldLabel}>Current Stage</span>
                <span style={{ ...styles.fieldValue, textTransform: 'capitalize' }}>
                  {currentDeal.status.replace(/_/g, ' ')}
                </span>
              </div>
              <div style={styles.field}>
                <span style={styles.fieldLabel}>Platform Commission Rate</span>
                <span style={styles.fieldValue}>{(commissionRate * 100).toFixed(0)}%</span>
              </div>
            </div>
          )}
          <div style={styles.btnRow}>
            <button onClick={() => setMode('accept')} style={styles.acceptBtn} className="sc-press">
              ✅ Accept Proposal
            </button>
            <button onClick={() => setMode('reject')} style={styles.rejectBtn} className="sc-press">
              ❌ Reject Proposal
            </button>
          </div>
        </div>
      )}

      {mode === 'accept' && (
        <div style={styles.card} className="sc-animate-scale-in">
          <h3 style={styles.cardTitle}>Accept Project Deal</h3>
          
          <div style={styles.inputGroup}>
            <label style={styles.label}>Agreed Deal Value (USD) *</label>
            <div style={styles.inputWrapper}>
              <span style={styles.inputPrefix}>$</span>
              <input
                type="number"
                value={dealValue}
                onChange={(e) => setDealValue(e.target.value)}
                placeholder="0.00"
                style={styles.priceInput}
                min="1"
                step="0.01"
                required
              />
            </div>
          </div>

          {parsedValue > 0 && (
            <div style={styles.commissionPreview}>
              <div style={styles.commRow}>
                <span>Agreed Price</span>
                <strong>${parsedValue.toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>
              </div>
              <div style={{ ...styles.commRow, borderBottom: 'none', paddingBottom: 0 }}>
                <span>Commission ({(commissionRate * 100).toFixed(0)}%)</span>
                <strong>${expectedCommission.toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>
              </div>
            </div>
          )}

          {/* Commission Policy notice */}
          <div style={styles.policyNoticeBox}>
            <AnimatedIcon name="warning" size={14} color="var(--sc-warning-text)" />
            <span style={{ marginLeft: 6 }}>
              <strong>Mandatory Policy:</strong> Platform commission is automatically handled upon project milestones.
              Account status is verified on deal completion to ensure compliance.
            </span>
          </div>

          {/* Mandatory agreement checkbox */}
          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={agreedToCommission}
              onChange={e => setAgreedToCommission(e.target.checked)}
              style={styles.checkbox}
            />
            <span>
              I confirm the total deal value of <strong>${parsedValue > 0 ? parsedValue.toLocaleString() : '—'}</strong> 
              and agree to authorize platform matching and payment services.
            </span>
          </label>

          <div style={styles.btnRow}>
            <button onClick={() => setMode('review')} style={styles.cancelBtn} className="sc-press">
              Cancel
            </button>
            <button
              onClick={handleAccept}
              disabled={isLoading || parsedValue <= 0 || !agreedToCommission}
              style={{
                ...styles.acceptBtn,
                opacity: parsedValue > 0 && agreedToCommission ? 1 : 0.5,
              }}
              className="sc-press"
            >
              {isLoading ? 'Confirming...' : '✅ Agree & Accept'}
            </button>
          </div>
        </div>
      )}

      {mode === 'reject' && (
        <div style={styles.card} className="sc-animate-scale-in">
          <h3 style={styles.cardTitle}>Reject Project Proposal</h3>
          <label style={styles.label}>Select Rejection Reason</label>
          
          <div style={styles.chipRow}>
            {REJECTION_REASONS.map((r) => (
              <button
                key={r}
                onClick={() => setRejectReason(r)}
                style={{
                  ...styles.chip,
                  background: rejectReason === r ? 'var(--sc-brand-primary-bg)' : 'var(--sc-bg-input)',
                  borderColor: rejectReason === r ? 'var(--sc-brand-primary)' : 'var(--sc-border)',
                  color: rejectReason === r ? 'var(--sc-brand-primary)' : 'var(--sc-text-secondary)',
                  fontWeight: rejectReason === r ? 700 : 500,
                }}
                className="sc-press"
              >
                {r}
              </button>
            ))}
          </div>
          
          <div style={styles.btnRow}>
            <button onClick={() => setMode('review')} style={styles.cancelBtn} className="sc-press">
              Cancel
            </button>
            <button
              onClick={handleReject}
              disabled={isLoading}
              style={styles.rejectBtn}
              className="sc-press"
            >
              {isLoading ? 'Rejecting...' : 'Confirm Rejection'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: { maxWidth: 520, margin: '0 auto', paddingBottom: 40, fontFamily: 'var(--sc-font-family)' },
  loading: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 60 },
  spinner: { width: 32, height: 32, border: '3px solid var(--sc-border)', borderTopColor: 'var(--sc-brand-primary)', borderRadius: '50%', animation: 'sc-spin 0.8s linear infinite', marginBottom: 12 },
  backBtn: { background: 'none', border: 'none', color: 'var(--sc-brand-primary)', fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: 0, marginBottom: 12 },
  title: { fontSize: 26, fontWeight: 800, color: 'var(--sc-text-primary)', margin: '0 0 4px', fontFamily: 'var(--sc-font-display)', letterSpacing: '-0.5px' },
  subtitle: { fontSize: 14, color: 'var(--sc-text-secondary)', margin: '0 0 20px' },
  
  errorBox: {
    display: 'flex',
    alignItems: 'center',
    background: 'var(--sc-error-bg)',
    border: '1px solid var(--sc-error)',
    borderRadius: 'var(--sc-radius-md)',
    padding: '12px 16px',
    color: 'var(--sc-error-text)',
    marginBottom: 20,
  },
  
  card: { background: 'var(--sc-bg-secondary)', borderRadius: 'var(--sc-radius-lg)', padding: 24, border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-card)' },
  cardTitle: { fontSize: 16, fontWeight: 700, color: 'var(--sc-text-primary)', margin: '0 0 16px', paddingBottom: 12, borderBottom: '1px solid var(--sc-border-light)', fontFamily: 'var(--sc-font-display)' },
  
  fieldList: { display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 },
  field: { display: 'flex', justifyContent: 'space-between', fontSize: 13.5, borderBottom: '1px solid var(--sc-border-light)', paddingBottom: 8 },
  fieldLabel: { color: 'var(--sc-text-secondary)', fontWeight: 500 },
  fieldValue: { color: 'var(--sc-text-primary)', fontWeight: 600 },

  inputGroup: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 13, fontWeight: 600, color: 'var(--sc-text-secondary)', marginBottom: 4 },
  
  inputWrapper: { position: 'relative', display: 'flex', alignItems: 'center' },
  inputPrefix: { position: 'absolute', left: 14, fontSize: 18, fontWeight: 700, color: 'var(--sc-text-tertiary)' },
  priceInput: { width: '100%', height: 48, padding: '0 14px 0 32px', border: '1px solid var(--sc-border)', borderRadius: 'var(--sc-radius-md)', fontSize: 18, fontWeight: 700, outline: 'none', color: 'var(--sc-text-primary)', background: 'var(--sc-bg-input)', boxSizing: 'border-box' },

  commissionPreview: { background: 'var(--sc-success-bg)', borderRadius: 'var(--sc-radius-md)', padding: 14, marginTop: 14, border: '1px solid var(--sc-success)' },
  commRow: { display: 'flex', justifyContent: 'space-between', fontSize: 13.5, color: 'var(--sc-success-text)', borderBottom: '1px solid rgba(16,185,129,0.2)', paddingBottom: 8, marginBottom: 8 },
  
  policyNoticeBox: { background: 'var(--sc-warning-bg)', border: '1px solid var(--sc-warning)', borderRadius: 'var(--sc-radius-md)', padding: '12px 14px', marginTop: 14, fontSize: 12, color: 'var(--sc-warning-text)', lineHeight: 1.5, display: 'flex', alignItems: 'flex-start' },
  
  checkboxLabel: { display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 16, cursor: 'pointer', fontSize: 12.5, color: 'var(--sc-text-secondary)', lineHeight: 1.5 },
  checkbox: { marginTop: 2, width: 16, height: 16, accentColor: 'var(--sc-brand-primary)', flexShrink: 0 },

  chipRow: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 },
  chip: { padding: '8px 14px', borderRadius: 20, border: '1px solid var(--sc-border)', fontSize: 12.5, cursor: 'pointer', fontFamily: 'inherit', transition: 'all var(--sc-transition-fast)' },
  
  btnRow: { display: 'flex', gap: 12, marginTop: 24 },
  acceptBtn: { flex: 1, height: 44, background: 'var(--sc-success)', color: '#FFF', border: 'none', borderRadius: 'var(--sc-radius-md)', fontSize: 13.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', boxShadow: 'var(--sc-shadow-sm)' },
  rejectBtn: { flex: 1, height: 44, background: 'var(--sc-error)', color: '#FFF', border: 'none', borderRadius: 'var(--sc-radius-md)', fontSize: 13.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', boxShadow: 'var(--sc-shadow-sm)' },
  cancelBtn: { flex: 1, height: 44, background: 'transparent', color: 'var(--sc-text-secondary)', border: '1px solid var(--sc-border)', borderRadius: 'var(--sc-radius-md)', fontSize: 13.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
};
